import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Check, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${BACKEND_URL}/api`;

/**
 * Suggests a few active DJs to follow when the user's feed is empty.
 * Sits under the Studio header only when the user is logged in AND has no follows yet.
 */
export default function FollowSuggestions() {
  const { user } = useAuth();
  const [feedEmpty, setFeedEmpty] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [followed, setFollowed] = useState({});

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/me/feed`).then(r => setFeedEmpty(r.data.length === 0)).catch(() => {});
    axios.get(`${API}/users/suggestions`).then(r => setSuggestions(r.data)).catch(() => {});
  }, [user]);

  const follow = async (id) => {
    try {
      await axios.post(`${API}/users/${id}/follow`);
      setFollowed(f => ({ ...f, [id]: true }));
      toast.success("Following.");
    } catch { toast.error("Follow failed"); }
  };

  if (!user || !feedEmpty || suggestions.length === 0) return null;

  return (
    <div className="border-y border-[#1E2638] bg-[#0B0E14]/95 backdrop-blur-md px-4 py-2 relative z-30" data-testid="follow-suggestions-strip">
      <div className="flex items-center gap-3 max-w-[1920px] mx-auto">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#FF2A6D] flex-shrink-0">
          <Sparkles className="w-3 h-3"/> DJs to follow
        </div>
        <div className="flex items-center gap-2 overflow-x-auto flex-1">
          {suggestions.map(s => (
            <div key={s.id} data-testid={`suggest-${s.id}`}
              className="flex items-center gap-3 px-3 py-1.5 rounded-full border border-[#1E2638] hover:border-[#FF2A6D] transition-colors">
              <Link to={`/u/${s.id}`} className="text-white font-bold text-xs whitespace-nowrap hover:text-[#FF2A6D]">{s.display_name}{s.is_premium && " · PRO"}</Link>
              <span className="text-[#94A3B8] font-mono text-[10px] whitespace-nowrap">{s.set_count} sets · {s.follower_count} followers</span>
              <button data-testid={`follow-${s.id}`} onClick={() => follow(s.id)} disabled={followed[s.id]}
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border transition-colors ${
                  followed[s.id]
                    ? "bg-[#10B981] text-black border-[#10B981]"
                    : "border-[#FF2A6D] text-[#FF2A6D] hover:bg-[#FF2A6D] hover:text-white"
                }`}>
                {followed[s.id] ? <Check className="w-3 h-3"/> : <><UserPlus className="w-3 h-3 inline mr-1"/> Follow</>}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
